import * as React from "react";
import { useParams } from "react-router-dom";
import { ScreenService } from "../../services/screen-service";
import { ScreenDesign } from "./screen-design";

/**
 * The designer route's element. It gives the designer a fresh `ScreenDesignContextProvider`
 * whenever the URL moves to a screen of ANOTHER app — the header's app switcher, browser Back
 * across apps, a link from chat. Within one app a screen change keeps the provider, as before.
 *
 * The provider loads its app once, on mount, and dozens of partitions hold state for that app
 * alone; nothing resets them if only the screen id changes. Leaving through the Apps page always
 * remounted it, because that page is a different route; a direct app-to-app move has to do the
 * same deliberately. The remount is safe because the outgoing provider's save and teardown are
 * ordered before the incoming load (`designer-teardown-gate.ts`).
 *
 * The app is compared by id: the mounted screen's app against the new screen's, both through the
 * cached screen → project lookup the provider's own load uses, so a screen in the open app costs
 * at most one request and never remounts.
 */
export const ScreenDesignRoute = (): React.ReactElement => {
  const { sdId } = useParams();
  const [generation, setGeneration] = React.useState<number>(0);
  const mountedSdId: React.MutableRefObject<string | undefined> = React.useRef<string | undefined>(sdId);

  React.useEffect((): (() => void) | undefined => {
    const fromSdId: string | undefined = mountedSdId.current;
    if (!sdId || !fromSdId || sdId === fromSdId) return undefined;

    // Both screens' apps come from the same cached lookup, so the answer does not depend on how
    // far the open provider's load has got: `window.kuika.activeProjectId` is only set once a
    // load lands, and reading it would miss a Back taken before that (or a stale id left by an
    // earlier visit).
    let isCurrent: boolean = true;
    Promise.all([ScreenService.getProjectIdFromScreenId(fromSdId), ScreenService.getProjectIdFromScreenId(sdId)])
      .then(([fromProjectId, toProjectId]: string[]): void => {
        if (!isCurrent || !fromProjectId || !toProjectId || fromProjectId === toProjectId) return;
        mountedSdId.current = sdId;
        setGeneration((previous: number): number => previous + 1);
      })
      .catch((): void => {
        // Unknown screen: the open provider's own load reports it; nothing to switch to.
      });
    return (): void => {
      isCurrent = false;
    };
  }, [sdId]);

  return <ScreenDesign key={generation} />;
};
